import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
} from "react";
import { authClient } from "@/lib/auth";
import { cn } from "@/lib/utils";
import {
  AGENT_VIEW_REPO_KEY,
  BUCKET_LABELS,
  BUCKET_ORDER,
  buildIdleComposerStatus,
  buildSessionDescription,
  deriveAgentViewRenderState,
  deriveSessionBucket,
  formatRelativeTime,
  formatAgentViewRecordingDuration,
  formatRepositoryCount,
  generateClientId,
  LOCAL_WORKING_INDICATOR_TTL_MS,
  optimisticReducer,
  readOptimisticLog,
  RECENT_SESSIONS_REFETCH_MS,
  RELATIVE_TIME_REFRESH_MS,
  REPO_QUERY_STALE_TIME_MS,
  SENDING_MIN_DISPLAY_MS,
  SENT_CONFIRMATION_DISPLAY_MS,
  TRANSCRIPT_REFETCH_MS,
  writeOptimisticLog,
  type AgentViewRecentSession,
  type AgentViewRepo,
  type AgentViewViewMode,
  type ComposerStatus,
  type DoStatusSnapshot,
  type OptimisticEntry,
  type OptimisticLog,
  type SessionBucket,
  type TranscriptMessage,
} from "@/lib/agent-view-shared";
import { AgentViewStatusIndicator } from "@/components/agent-view/StatusIndicator";
import { SlashCommandMenu } from "@/components/agent-view/SlashCommandMenu";
import { RepoPickerMenu } from "@/components/agent-view/RepoPickerMenu";
import {
  AgentViewModelMenu,
  type AgentViewModelMenuOption,
} from "@/components/agent-view/AgentViewModelMenu";
import { OpenCodeTerminalsView } from "@/components/agent-view/OpenCodeTerminalsView";
import { AgentViewVoiceStatus } from "@/components/agent-view/AgentViewVoiceStatus";
import { useAgentViewComposer } from "@/components/agent-view/use-agent-view-composer";
import { useVoiceTranscription } from "@/hooks/use-voice-transcription";
import { useProviders, type ProviderModel } from "@/hooks/use-providers";
import { useModelPrefs, type ModelKey } from "@/stores/model-prefs";
import {
  formatThinkingVariantLabel,
  getThinkingVariants,
  normalizeThinkingVariant,
  resolveEffectiveThinkingVariant,
  THINKING_OFF_VARIANT,
} from "@/config/model-capabilities";
import type { SlashCommandDefinition } from "@/lib/agent-view-slash-commands";
import {
  applyOptimisticSessionMembershipToSessionListCaches,
  captureSessionCacheMutationSnapshot,
  restoreSessionCacheMutationSnapshot,
  selectSessionCacheMutationSource,
} from "@/lib/session-membership-cache";

// -----------------------------------------------------------------------------
// AgentView — terminal-style launcher (centered floating card)
// -----------------------------------------------------------------------------
// A small, vertically + horizontally centered terminal that lets the user:
//   1. Browse their unarchived sessions (Needs input / Working / Completed).
//   2. Open a session inline (Enter on the row) and read its transcript,
//      pulled in the background from the SessionDO via a lightweight
//      `/api/agent-view/sessions/:id/transcript` route.
//   3. Send a follow-up message right inside the terminal — same composer,
//      same Sending → Sent ✓ animation. The user can switch back to the list
//      (Esc) immediately and the message keeps queueing in the background.
//   4. Run slash commands. Typing `/` brings up an autocomplete menu of
//      built-in commands:
//        /repository — opens an inline repo picker (arrow keys + Enter)
//        /model      — opens an inline model + thinking picker
//        /expand     — opens native OpenCode UI frames for active OpenCode
//                      sandboxes only
//      The slash menu is rendered immediately above the composer; arrow
//      keys move the cursor, Enter dispatches, Tab autocompletes the typed
//      command name, Esc closes the menu.
//
// All keyboard-driven. No sidebar, no command palette, no WebSocket — the
// parent `_authed.tsx` layout strips those for `/agent-view`. The transcript
// endpoint is a one-shot HTTP call: cheap, refetched on focus, and explicitly
// not subscribed to live updates so the UI never waits on socket reconnects.
// -----------------------------------------------------------------------------

export const Route = createFileRoute("/_authed/agent-view")({
  component: AgentViewPage,
});

// The uploads API explicitly allows this pseudo-session id for voice drafts
// recorded before a real Agent session exists. List mode uses it, then sends
// the transcription through AgentView's normal new-session path.
const AGENT_VIEW_DRAFT_VOICE_SESSION_ID = "dashboard";

// AgentView uses the same default OpenCode-backed agent type as the lightweight
// API route. If AgentView grows an agent picker, this should be threaded from
// that picker instead of staying fixed here.
const AGENT_VIEW_MODEL_AGENT_TYPE = "opencode";
// Keyboard menus advance one row per arrow key press. Keeping this named makes
// wraparound behavior easier to adjust if we later add page-wise navigation.
const MODEL_MENU_NAVIGATION_STEP = 1;
// Row summaries can trigger paid Gemini calls on first generation. Capping the
// visible awaiting-input batch keeps AgentView from fanning out across a large
// backlog while still covering the rows the user is most likely to inspect.
const AGENT_VIEW_ROW_SUMMARY_TARGET_LIMIT = 8;
// Cache row-summary query results in the browser long enough to avoid refetching
// while the user navigates around AgentView; the server-side cache remains the
// durable source keyed by transcript fingerprint.
const AGENT_VIEW_ROW_SUMMARY_STALE_TIME_MS = 5 * 60 * 1000;

function sameModelKey(args: { left: ModelKey | null; right: ModelKey | null }): boolean {
  return Boolean(
    args.left
      && args.right
      && args.left.providerID === args.right.providerID
      && args.left.modelID === args.right.modelID,
  );
}

function findModelDetail(args: {
  providers: Array<{ id: string; active: boolean; models: ProviderModel[] }>;
  model: ModelKey | null;
}): ProviderModel | null {
  if (!args.model) return null;
  const provider = args.providers.find((candidate) => candidate.id === args.model?.providerID);
  if (!provider?.active) return null;
  return provider.models.find(
    (candidate) => candidate.id === args.model?.modelID && candidate.active,
  ) ?? null;
}

function buildLocalRunningDoStatusSnapshot(args: {
  row: AgentViewRecentSession;
}): DoStatusSnapshot {
  const existing = args.row.doStatusSnapshot ?? null;
  return {
    activityState: "running",
    lastFinishReason: null,
    lastFinishedEventId: existing?.lastFinishedEventId ?? null,
    lastActivityEventId: existing?.lastActivityEventId ?? null,
    prState: existing?.prState ?? args.row.prState,
    prUrl: existing?.prUrl ?? args.row.prUrl,
    prNumber: existing?.prNumber ?? args.row.prNumber,
    lifecycleStatus: existing?.lifecycleStatus ?? args.row.lifecycleStatus,
  };
}

function resolveDefaultAgentViewModel(args: {
  providers: Array<{ id: string; active: boolean; models: ProviderModel[] }>;
  defaults: Record<string, string>;
}): ModelKey | null {
  for (const provider of args.providers) {
    if (!provider.active) continue;
    const defaultModelId = args.defaults[provider.id];
    const defaultModel = provider.models.find(
      (model) => model.active && model.id === defaultModelId,
    );
    if (defaultModel) {
      return { providerID: provider.id, modelID: defaultModel.id };
    }
  }

  for (const provider of args.providers) {
    if (!provider.active) continue;
    const firstActiveModel = provider.models.find((model) => model.active);
    if (firstActiveModel) {
      return { providerID: provider.id, modelID: firstActiveModel.id };
    }
  }

  return null;
}

function AgentViewPage() {
  const queryClient = useQueryClient();
  const { data: session } = authClient.useSession();
  const userName = (session?.user?.name ?? session?.user?.email ?? "you").trim();
  const userFirstName = userName.split(/\s+/)[0] ?? userName;

  // Three modes: "list" (browse), "thread" (read+reply to one), and
  // "expanded" (native OpenCode UI frames for active OpenCode sandboxes only).
  const [viewMode, setViewMode] = useState<AgentViewViewMode>("list");
  const [openedSessionId, setOpenedSessionId] = useState<string | null>(null);

  const [draft, setDraft] = useState("");
  const [selectedSessionId, setSelectedSessionId] = useState<string | null>(null);
  const [composerStatus, setComposerStatus] = useState<ComposerStatus>(
    buildIdleComposerStatus,
  );
  const [now, setNow] = useState(() => Date.now());
  const [helpOpen, setHelpOpen] = useState(false);
  const [archiveConfirmation, setArchiveConfirmation] = useState<{
    sessionId: string;
    choice: "yes" | "no";
  } | null>(null);
  const [optimisticallyArchivedSessionIds, setOptimisticallyArchivedSessionIds] = useState<Set<string>>(
    () => new Set(),
  );
  const [pendingRepoId, setPendingRepoId] = useState<string | null>(() => {
    try {
      return localStorage.getItem(AGENT_VIEW_REPO_KEY);
    } catch {
      return null;
    }
  });
  const [optimisticLog, dispatchOptimistic] = useReducer(
    optimisticReducer,
    {} as OptimisticLog,
  );
  const inputRef = useRef<HTMLInputElement | null>(null);
  const listRef = useRef<HTMLDivElement | null>(null);
  const threadScrollerRef = useRef<HTMLDivElement | null>(null);

  // Hydrate optimistic log from localStorage once.
  useLayoutEffect(() => {
    dispatchOptimistic({ type: "hydrate", log: readOptimisticLog() });
  }, []);

  // Persist optimistic log on every change.
  useEffect(() => {
    writeOptimisticLog(optimisticLog);
  }, [optimisticLog]);

  // ---------------------------------------------------------------------------
  // Recent sessions (always running so the list stays warm even from thread)
  // ---------------------------------------------------------------------------
  const recentSessionsQuery = useQuery({
    queryKey: ["agent-view", "recent-sessions"],
    queryFn: async ({ signal }): Promise<AgentViewRecentSession[]> => {
      const baseUrl = import.meta.env.VITE_API_URL || "";
      const res = await fetch(`${baseUrl}/api/agent-view/recent-sessions`, {
        credentials: "include",
        signal,
      });
      if (!res.ok) {
        throw new Error("Failed to load recent sessions");
      }
      const body = (await res.json()) as { sessions?: AgentViewRecentSession[] };
      return Array.isArray(body.sessions) ? body.sessions : [];
    },
    refetchInterval: RECENT_SESSIONS_REFETCH_MS,
    refetchOnWindowFocus: true,
  });
  const sessions = recentSessionsQuery.data ?? [];

  const locallyWorkingSessionIds = useMemo(() => {
    const ids = new Set<string>();
    for (const [sessionId, entries] of Object.entries(optimisticLog)) {
      if (!sessionId) continue;
      const hasRecentQueuedMessage = entries.some((entry) => {
        if (entry.status === "failed") return false;
        return now - entry.createdAt <= LOCAL_WORKING_INDICATOR_TTL_MS;
      });
      if (hasRecentQueuedMessage) ids.add(sessionId);
    }
    return ids;
  }, [now, optimisticLog]);

  const displaySessions = useMemo(
    () =>
      sessions
        .filter((row) => !optimisticallyArchivedSessionIds.has(row.id))
        .map((row) =>
          locallyWorkingSessionIds.has(row.id) && row.status !== "error"
            ? {
                ...row,
                status: "running",
                updatedAt: new Date(now).toISOString(),
                doStatusSnapshot: buildLocalRunningDoStatusSnapshot({ row }),
              }
            : row,
        ),
    [locallyWorkingSessionIds, now, optimisticallyArchivedSessionIds, sessions],
  );

  // ---------------------------------------------------------------------------
  // Repos (light, cached)
  // ---------------------------------------------------------------------------
  const reposQuery = useQuery({
    queryKey: ["agent-view", "repos"],
    queryFn: async ({ signal }): Promise<AgentViewRepo[]> => {
      const baseUrl = import.meta.env.VITE_API_URL || "";
      const res = await fetch(`${baseUrl}/api/repos`, { credentials: "include", signal });
      if (!res.ok) throw new Error("Failed to load repositories");
      const body = (await res.json()) as unknown;
      if (!Array.isArray(body)) return [];
      return body
        .filter((row): row is AgentViewRepo & { lifecycleStatus?: string } => {
          return Boolean(
            row
              && typeof row === "object"
              && typeof (row as AgentViewRepo).id === "string"
              && typeof (row as AgentViewRepo).githubFullName === "string",
          );
        })
        .filter((row) => row.lifecycleStatus !== "deleted" && row.lifecycleStatus !== "deleting");
    },
    staleTime: REPO_QUERY_STALE_TIME_MS,
  });
  const repos = reposQuery.data ?? [];

  // ---------------------------------------------------------------------------
  // Model + thinking selection for new AgentView sessions and replies.
  // ---------------------------------------------------------------------------
  const providersQuery = useProviders(null, false, AGENT_VIEW_MODEL_AGENT_TYPE);
  const agentViewProviders = providersQuery.data?.providers ?? [];
  const agentViewProviderDefaults = providersQuery.data?.defaults ?? {};
  const setModelVariantPref = useModelPrefs((state) => state.setVariant);
  const getModelVariantPref = useModelPrefs((state) => state.getVariant);
  const pushRecentModel = useModelPrefs((state) => state.pushRecent);

  const [selectedModel, setSelectedModel] = useState<ModelKey | null>(null);
  const [selectedThinkingVariant, setSelectedThinkingVariant] = useState<string | null>(null);
  const [modelMenuOpen, setModelMenuOpen] = useState(false);
  const [modelMenuActiveIndex, setModelMenuActiveIndex] = useState(0);

  const defaultModel = useMemo(
    () => resolveDefaultAgentViewModel({
      providers: agentViewProviders,
      defaults: agentViewProviderDefaults,
    }),
    [agentViewProviderDefaults, agentViewProviders],
  );
  const effectiveModel = selectedModel ?? defaultModel;
  const effectiveModelDetail = useMemo(
    () => findModelDetail({ providers: agentViewProviders, model: effectiveModel }),
    [agentViewProviders, effectiveModel],
  );
  const thinkingVariants = useMemo(
    () => effectiveModel
      ? getThinkingVariants(
          effectiveModel.providerID,
          effectiveModel.modelID,
          effectiveModelDetail?.variants,
        )
      : [],
    [effectiveModel, effectiveModelDetail?.variants],
  );
  const normalizedSelectedThinkingVariant = normalizeThinkingVariant(selectedThinkingVariant);
  const effectiveThinkingVariant = effectiveModel
    ? resolveEffectiveThinkingVariant({
        providerID: effectiveModel.providerID,
        modelID: effectiveModel.modelID,
        requestedVariant: normalizedSelectedThinkingVariant,
        runtimeVariants: effectiveModelDetail?.variants,
      })
    : null;
  const modelDisplayLabel = useMemo(() => {
    if (!effectiveModel) {
      return providersQuery.data?.loadingModels ? "loading model" : "default model";
    }
    const provider = agentViewProviders.find((candidate) => candidate.id === effectiveModel.providerID);
    const model = provider?.models.find((candidate) => candidate.id === effectiveModel.modelID);
    return model?.name ?? effectiveModel.modelID;
  }, [agentViewProviders, effectiveModel, providersQuery.data?.loadingModels]);
  const thinkingDisplayLabel = effectiveThinkingVariant
    ? formatThinkingVariantLabel(effectiveThinkingVariant)
    : "Off";
  const modelStatusLabel = `model ${modelDisplayLabel} · thinking ${thinkingDisplayLabel}`;

  const modelMenuOptions = useMemo<AgentViewModelMenuOption[]>(() => {
    const options: AgentViewModelMenuOption[] = [];
    for (const provider of agentViewProviders) {
      if (!provider.active) continue;
      for (const model of provider.models) {
        if (!model.active) continue;
        const modelKey = { providerID: provider.id, modelID: model.id };
        options.push({
          id: `model:${provider.id}:${model.id}`,
          kind: "model",
          label: model.name,
          description: provider.name,
          selected: sameModelKey({ left: effectiveModel, right: modelKey }),
        });
      }
    }

    if (effectiveModel) {
      options.push({
        id: "thinking:off",
        kind: "thinking",
        label: "Off",
        description: "disable explicit reasoning",
        selected: effectiveThinkingVariant === THINKING_OFF_VARIANT || effectiveThinkingVariant === null,
      });
      for (const variant of thinkingVariants) {
        options.push({
          id: `thinking:${variant}`,
          kind: "thinking",
          label: formatThinkingVariantLabel(variant),
          description: "reasoning level",
          selected: effectiveThinkingVariant === variant,
        });
      }
    } else {
      options.push({
        id: "thinking:none",
        kind: "thinking",
        label: "Thinking unavailable",
        description: "pick a model first",
        selected: false,
        disabled: true,
      });
    }

    return options;
  }, [agentViewProviders, effectiveModel, effectiveThinkingVariant, thinkingVariants]);

  useEffect(() => {
    setModelMenuActiveIndex((current) => {
      if (modelMenuOptions.length === 0) return 0;
      return Math.min(current, modelMenuOptions.length - MODEL_MENU_NAVIGATION_STEP);
    });
  }, [modelMenuOptions.length]);

  // ---------------------------------------------------------------------------
  // Bucketing for list mode
  // ---------------------------------------------------------------------------
  const grouped = useMemo(() => {
    const buckets: Record<SessionBucket, AgentViewRecentSession[]> = {
      needs_input: [],
      working: [],
      completed: [],
      idle: [],
    };
    for (const row of displaySessions) {
      const bucket = deriveSessionBucket({
        status: row.status,
        lifecycleStatus: row.lifecycleStatus,
        prState: row.prState,
        doSnapshot: row.doStatusSnapshot ?? null,
      });
      buckets[bucket].push(row);
    }
    return buckets;
  }, [displaySessions]);

  const flatList = useMemo(() => {
    const out: AgentViewRecentSession[] = [];
    for (const bucket of BUCKET_ORDER) {
      out.push(...grouped[bucket]);
    }
    return out;
  }, [grouped]);

  const stats = useMemo(() => ({
    needs_input: grouped.needs_input.length,
    working: grouped.working.length,
    completed: grouped.completed.length,
    idle: grouped.idle.length,
  }), [grouped]);

  const rowSummaryTargets = useMemo(
    () =>
      flatList
        .filter((row) => {
          const renderState = deriveAgentViewRenderState({
            status: row.status,
            lifecycleStatus: row.lifecycleStatus,
            prState: row.prState,
            doSnapshot: row.doStatusSnapshot ?? null,
          });
          return renderState === "waiting_input_dot" || renderState === "finished_unviewed_dot";
        })
        .slice(0, AGENT_VIEW_ROW_SUMMARY_TARGET_LIMIT),
    [flatList],
  );

  const rowSummaryQueries = useQueries({
    queries: rowSummaryTargets.map((row) => ({
      queryKey: ["agent-view", "row-summary", row.id, row.doStatusSnapshot?.lastFinishedEventId ?? null],
      queryFn: async ({ signal }: { signal?: AbortSignal }): Promise<string | null> => {
        const baseUrl = import.meta.env.VITE_API_URL || "";
        const res = await fetch(`${baseUrl}/api/agent-view/sessions/${row.id}/row-summary`, {
          credentials: "include",
          signal,
        });
        if (!res.ok) return null;
        const body = (await res.json()) as { summary?: unknown };
        return typeof body.summary === "string" && body.summary.trim().length > 0
          ? body.summary.trim()
          : null;
      },
      staleTime: AGENT_VIEW_ROW_SUMMARY_STALE_TIME_MS,
      retry: false,
    })),
  });

  const rowSummaryBySessionId = useMemo(() => {
    const summaries = new Map<string, string>();
    for (let index = 0; index < rowSummaryTargets.length; index += 1) {
      const summary = rowSummaryQueries[index]?.data;
      if (summary) summaries.set(rowSummaryTargets[index].id, summary);
    }
    return summaries;
  }, [rowSummaryQueries, rowSummaryTargets]);

  // Keep the keyboard cursor on a still-existing row.
  useEffect(() => {
    if (selectedSessionId === null) return;
    if (!flatList.some((row) => row.id === selectedSessionId)) {
      setSelectedSessionId(flatList[0]?.id ?? null);
    }
    if (
      archiveConfirmation !== null
      && !flatList.some((row) => row.id === archiveConfirmation.sessionId)
    ) {
      setArchiveConfirmation(null);
    }
  }, [archiveConfirmation, flatList, selectedSessionId]);

  useLayoutEffect(() => {
    if (selectedSessionId !== null) return;
    if (flatList.length === 0) return;
    setSelectedSessionId(flatList[0].id);
  }, [flatList, selectedSessionId]);

  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), RELATIVE_TIME_REFRESH_MS);
    return () => window.clearInterval(id);
  }, []);

  useEffect(() => {
    if (pendingRepoId === null) return;
    try {
      localStorage.setItem(AGENT_VIEW_REPO_KEY, pendingRepoId);
    } catch {
      // ignore
    }
  }, [pendingRepoId]);

  const fallbackRepoId = pendingRepoId ?? repos[0]?.id ?? null;
  const fallbackRepo = useMemo(() => {
    if (!fallbackRepoId) return null;
    return repos.find((r) => r.id === fallbackRepoId) ?? repos[0] ?? null;
  }, [fallbackRepoId, repos]);

  const selectedSession = useMemo<AgentViewRecentSession | null>(() => {
    if (!selectedSessionId) return null;
    return flatList.find((row) => row.id === selectedSessionId) ?? null;
  }, [flatList, selectedSessionId]);

  const openedSession = useMemo<AgentViewRecentSession | null>(() => {
    if (!openedSessionId) return null;
    return displaySessions.find((row) => row.id === openedSessionId) ?? null;
  }, [displaySessions, openedSessionId]);

  // ---------------------------------------------------------------------------
  // Transcript (only fetched when in thread mode)
  // ---------------------------------------------------------------------------
  const transcriptQuery = useQuery({
    queryKey: ["agent-view", "transcript", openedSessionId],
    enabled: viewMode === "thread" && Boolean(openedSessionId),
    queryFn: async ({ signal }): Promise<TranscriptMessage[]> => {
      if (!openedSessionId) return [];
      const baseUrl = import.meta.env.VITE_API_URL || "";
      const res = await fetch(
        `${baseUrl}/api/agent-view/sessions/${openedSessionId}/transcript`,
        { credentials: "include", signal },
      );
      if (!res.ok) {
        throw new Error("Failed to load transcript");
      }
      const body = (await res.json()) as { messages?: TranscriptMessage[] };
      return Array.isArray(body.messages) ? body.messages : [];
    },
    refetchInterval: TRANSCRIPT_REFETCH_MS,
    refetchOnWindowFocus: true,
  });
  const transcript = transcriptQuery.data ?? [];

  // Live DO probe for the open thread.
  const statusQuery = useQuery({
    queryKey: ["agent-view", "status", openedSessionId],
    enabled: viewMode === "thread" && Boolean(openedSessionId),
    queryFn: async ({ signal }): Promise<DoStatusSnapshot | null> => {
      if (!openedSessionId) return null;
      const baseUrl = import.meta.env.VITE_API_URL || "";
      const res = await fetch(
        `${baseUrl}/api/agent-view/sessions/${openedSessionId}/status`,
        { credentials: "include", signal },
      );
      if (!res.ok) return null;
      return (await res.json()) as DoStatusSnapshot;
    },
    refetchInterval: TRANSCRIPT_REFETCH_MS,
    refetchOnWindowFocus: true,
    retry: false,
  });
  const openThreadDoStatus = statusQuery.data ?? null;

  // GC the optimistic log: any user-role line whose text matches a transcript
  // entry has clearly landed; drop it so we don't double-render.
  useEffect(() => {
    if (!openedSessionId) return;
    const userTexts = new Set(
      transcript
        .filter((message) => message.role === "user")
        .map((message) => message.content.trim()),
    );
    if (userTexts.size === 0) return;
    dispatchOptimistic({
      type: "removeMatched",
      sessionId: openedSessionId,
      transcriptTexts: userTexts,
    });
  }, [openedSessionId, transcript]);

  const optimisticForOpened = useMemo(() => {
    if (!openedSessionId) return [];
    return optimisticLog[openedSessionId] ?? [];
  }, [openedSessionId, optimisticLog]);

  const hasPendingOptimisticForOpened = useMemo(
    () =>
      optimisticForOpened.some(
        (entry) =>
          entry.status !== "failed" &&
          now - entry.createdAt <= LOCAL_WORKING_INDICATOR_TTL_MS,
      ),
    [now, optimisticForOpened],
  );

  // Merged display list for the thread view.
  const threadDisplay = useMemo(() => {
    type ThreadDisplayItem =
      | { kind: "transcript"; message: TranscriptMessage; key: string }
      | { kind: "optimistic"; entry: OptimisticEntry; key: string };
    const items: ThreadDisplayItem[] = [];
    for (const message of transcript) {
      items.push({
        kind: "transcript",
        message,
        key: `t:${message.timestamp}:${message.role}:${message.content.length}`,
      });
    }
    for (const entry of optimisticForOpened) {
      const alreadyInTranscript = transcript.some(
        (message) =>
          message.role === "user" && message.content.trim() === entry.text.trim(),
      );
      if (alreadyInTranscript && entry.status === "sent") continue;
      items.push({
        kind: "optimistic",
        entry,
        key: `o:${entry.id}`,
      });
    }
    return items;
  }, [optimisticForOpened, transcript]);

  // Auto-scroll to bottom whenever the thread length changes.
  useEffect(() => {
    if (viewMode !== "thread") return;
    const el = threadScrollerRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [threadDisplay.length, viewMode]);

  // ---------------------------------------------------------------------------
  // Cache patch helpers shared by AgentView send paths.
  // ---------------------------------------------------------------------------
  // The previous version inlined this inside `sendMutation.onSuccess`. We hoist
  // it so each send path can refresh the recent-sessions list after a reply
  // without re-implementing the same setQueryData transform.
  const patchRecentSessionsAfterSend = useCallback(
    (args: {
      kind: "new" | "reply";
      sessionId: string;
      title?: string;
      agentType?: string;
      repositoryId?: string;
      requestedRepositoryId: string | null;
      promptText: string;
      invalidate?: boolean;
    }) => {
      const nowIso = new Date().toISOString();
      queryClient.setQueryData<AgentViewRecentSession[] | undefined>(
        ["agent-view", "recent-sessions"],
        (current) => {
          if (args.kind === "reply") {
            return (current ?? []).map((row) =>
              row.id === args.sessionId
                ? { ...row, status: "running", updatedAt: nowIso }
                : row,
            );
          }
          const repo = repos.find((row) => row.id === args.repositoryId)
            ?? repos.find((row) => row.id === args.requestedRepositoryId)
            ?? null;
          const repositoryFullName = repo?.githubFullName ?? null;
          const repositoryName = repositoryFullName?.split("/").at(-1) ?? null;
          const optimisticSession: AgentViewRecentSession = {
            id: args.sessionId,
            title: args.title ?? args.promptText,
            repositoryId: args.repositoryId ?? args.requestedRepositoryId ?? "",
            agentType: args.agentType ?? "opencode",
            status: "running",
            lifecycleStatus: "active",
            prState: null,
            prUrl: null,
            prNumber: null,
            branch: null,
            createdAt: nowIso,
            updatedAt: nowIso,
            repositoryName,
            repositoryFullName,
            repositoryDefaultBranch: null,
          };
          return [
            optimisticSession,
            ...(current ?? []).filter((row) => row.id !== args.sessionId),
          ];
        },
      );
      if (args.invalidate !== false) {
        queryClient.invalidateQueries({ queryKey: ["agent-view", "recent-sessions"] });
      }
      queryClient.invalidateQueries({
        queryKey: ["agent-view", "status", args.sessionId],
      });
    },
    [queryClient, repos],
  );

  // The shared "do the thing" function used by AgentView's composer. Returns
  // the resolved sessionId so callers can update local UI state on success.
  const performSend = useCallback(
    async (args: {
      text: string;
      sessionId: string | null;
      newSessionId?: string;
      repositoryId: string | null;
      model: ModelKey | null;
      thinkingVariant: string | null;
    }): Promise<{ kind: "new" | "reply"; sessionId: string }> => {
      const baseUrl = import.meta.env.VITE_API_URL || "";
      const startedAt = Date.now();
      const res = await fetch(`${baseUrl}/api/agent-view/messages`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          text: args.text,
          sessionId: args.sessionId,
          newSessionId: args.newSessionId,
          repositoryId: args.repositoryId,
          model: args.model,
          thinking: args.thinkingVariant,
          clientMessageId: generateClientId(),
        }),
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => null)) as { error?: string; message?: string } | null;
        const message = body?.message ?? body?.error ?? `Failed to send (${res.status})`;
        throw new Error(message);
      }
      const data = (await res.json()) as {
        ok: boolean;
        kind: "new" | "reply";
        sessionId: string;
        clientMessageId: string;
        title?: string;
        agentType?: string;
        repositoryId?: string;
      };
      const elapsed = Date.now() - startedAt;
      const remaining = SENDING_MIN_DISPLAY_MS - elapsed;
      if (remaining > 0) {
        await new Promise((resolve) => window.setTimeout(resolve, remaining));
      }
      patchRecentSessionsAfterSend({
        kind: data.kind,
        sessionId: data.sessionId,
        title: data.title,
        agentType: data.agentType,
        repositoryId: data.repositoryId,
        requestedRepositoryId: args.repositoryId,
        promptText: args.text,
      });
      return { kind: data.kind, sessionId: data.sessionId };
    },
    [patchRecentSessionsAfterSend],
  );

  // ---------------------------------------------------------------------------
  // Mutations
  // ---------------------------------------------------------------------------
  const sendMutation = useMutation({
    mutationFn: async (vars: {
      optimisticId: string;
      text: string;
      sessionId: string | null;
      newSessionId?: string;
      repositoryId: string | null;
      model: ModelKey | null;
      thinkingVariant: string | null;
      sendStartedAt: number;
    }) => {
      return performSend({
        text: vars.text,
        sessionId: vars.sessionId,
        newSessionId: vars.newSessionId,
        repositoryId: vars.repositoryId,
        model: vars.model,
        thinkingVariant: vars.thinkingVariant,
      });
    },
    onSuccess: (data, vars) => {
      // Promote the optimistic entry under the resolved session id. New
      // sessions originally recorded the entry under "" because we didn't
      // know the id yet; in that case we move it to the real id and drop
      // the placeholder so it doesn't linger forever in localStorage.
      const previousSessionId = vars.newSessionId ?? vars.sessionId ?? "";
      if (previousSessionId !== data.sessionId) {
        dispatchOptimistic({
          type: "removeById",
          sessionId: previousSessionId,
          id: vars.optimisticId,
        });
        dispatchOptimistic({
          type: "append",
          entry: {
            id: vars.optimisticId,
            sessionId: data.sessionId,
            text: vars.text,
            status: "sent",
            errorMessage: null,
            createdAt: vars.sendStartedAt,
          },
        });
      } else {
        dispatchOptimistic({
          type: "patch",
          sessionId: data.sessionId,
          id: vars.optimisticId,
          patch: { status: "sent" },
        });
      }
      setComposerStatus({
        state: "sent",
        startedAt: Date.now(),
        errorMessage: null,
        sentToSessionId: data.sessionId,
        sentKind: data.kind,
      });
      setSelectedSessionId(data.sessionId);
      if (data.kind === "new") {
        setOpenedSessionId(data.sessionId);
        setViewMode("thread");
      }
      // Refresh the active thread's transcript shortly so the optimistic
      // entry gets replaced by the real one.
      if (viewMode === "thread" && openedSessionId === data.sessionId) {
        queryClient.invalidateQueries({
          queryKey: ["agent-view", "transcript", data.sessionId],
        });
      }
    },
    onError: (error, vars) => {
      const sessionId = vars.newSessionId ?? vars.sessionId ?? "";
      dispatchOptimistic({
        type: "patch",
        sessionId,
        id: vars.optimisticId,
        patch: {
          status: "failed",
          errorMessage: error instanceof Error ? error.message : "Failed to send",
        },
      });
      setComposerStatus({
        state: "failed",
        startedAt: Date.now(),
        errorMessage: error instanceof Error ? error.message : "Failed to send",
        sentToSessionId: null,
        sentKind: null,
      });
      if (vars.newSessionId) {
        queryClient.setQueryData<AgentViewRecentSession[] | undefined>(
          ["agent-view", "recent-sessions"],
          (current) => (current ?? []).filter((row) => row.id !== vars.newSessionId),
        );
        if (openedSessionId === vars.newSessionId) {
          setOpenedSessionId(null);
          setViewMode("list");
        }
      }
    },
  });

  const archiveMutation = useMutation({
    mutationFn: async (vars: { sessionId: string }) => {
      const baseUrl = import.meta.env.VITE_API_URL || "";
      const res = await fetch(
        `${baseUrl}/api/sessions/${vars.sessionId}/archive`,
        { method: "POST", credentials: "include" },
      );
      if (!res.ok) {
        const body = (await res.json().catch(() => null)) as { error?: string } | null;
        throw new Error(body?.error ?? `Failed to archive (${res.status})`);
      }
      return vars.sessionId;
    },
    onMutate: async (vars) => {
      await queryClient.cancelQueries({ queryKey: ["agent-view", "recent-sessions"] });
      await queryClient.cancelQueries({ queryKey: ["sessions"] });
      await queryClient.cancelQueries({ queryKey: ["session", vars.sessionId] });
      const previous = queryClient.getQueryData<AgentViewRecentSession[]>([
        "agent-view",
        "recent-sessions",
      ]);
      const recentSession = previous?.find((row) => row.id === vars.sessionId) ?? null;
      const snapshot = captureSessionCacheMutationSnapshot({
        queryClient,
        sessionId: vars.sessionId,
        repoId: recentSession?.repositoryId ?? null,
      });
      const nowIso = new Date().toISOString();
      const sourceItem = selectSessionCacheMutationSource({
        snapshot,
        sessionId: vars.sessionId,
      });
      const archivedSession = sourceItem
        ? { ...sourceItem, archivedAt: nowIso }
        : recentSession
          ? { ...recentSession, archivedAt: nowIso }
          : null;
      setOptimisticallyArchivedSessionIds((current) => {
        const next = new Set(current);
        next.add(vars.sessionId);
        return next;
      });
      queryClient.setQueryData(["session", vars.sessionId], (old: unknown) => {
        if (!old || typeof old !== "object") return archivedSession ?? old;
        return { ...(old as Record<string, unknown>), archivedAt: nowIso };
      });
      applyOptimisticSessionMembershipToSessionListCaches({
        queryClient,
        snapshot,
        sessionId: vars.sessionId,
        item: archivedSession,
      });
      queryClient.setQueryData<AgentViewRecentSession[] | undefined>(
        ["agent-view", "recent-sessions"],
        (current) => (current ?? []).filter((row) => row.id !== vars.sessionId),
      );
      return { previous, snapshot };
    },
    onError: (error, vars, context) => {
      setOptimisticallyArchivedSessionIds((current) => {
        const next = new Set(current);
        next.delete(vars.sessionId);
        return next;
      });
      if (context?.previous) {
        queryClient.setQueryData(
          ["agent-view", "recent-sessions"],
          context.previous,
        );
      }
      if (context?.snapshot) {
        restoreSessionCacheMutationSnapshot({
          queryClient,
          sessionId: vars.sessionId,
          snapshot: context.snapshot,
        });
      }
      toast.error("Failed to archive thread", {
        description: error instanceof Error ? error.message : "Please try again.",
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["agent-view", "recent-sessions"] });
      queryClient.invalidateQueries({ queryKey: ["sessions"] });
    },
  });

  // ---------------------------------------------------------------------------
  // Auto-clear composer state
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (composerStatus.state !== "sent") return;
    const id = window.setTimeout(() => {
      setComposerStatus(buildIdleComposerStatus());
    }, SENT_CONFIRMATION_DISPLAY_MS);
    return () => window.clearTimeout(id);
  }, [composerStatus.state, composerStatus.startedAt]);

  // ---------------------------------------------------------------------------
  // Submit
  // ---------------------------------------------------------------------------
  const submitText = useCallback((args: { text: string }) => {
    if (composerStatus.state === "sending") return;
    const text = args.text.trim();
    if (!text) return;
    const sessionId = viewMode === "thread" ? openedSessionId : null;
    const repositoryId = sessionId ? null : (fallbackRepo?.id ?? null);
    if (!sessionId && !repositoryId) {
      setComposerStatus({
        state: "failed",
        startedAt: Date.now(),
        errorMessage: "Connect a repository first.",
        sentToSessionId: null,
        sentKind: null,
      });
      return;
    }
    const newSessionId = sessionId ? undefined : crypto.randomUUID();
    const targetSessionId = sessionId ?? newSessionId ?? "";
    const optimisticId = generateClientId();
    const startedAt = Date.now();
    dispatchOptimistic({
      type: "append",
      entry: {
        id: optimisticId,
        sessionId: targetSessionId,
        text,
        status: "sending",
        errorMessage: null,
        createdAt: startedAt,
      },
    });
    setComposerStatus({
      state: "sending",
      startedAt,
      errorMessage: null,
      sentToSessionId: null,
      sentKind: null,
    });
    setDraft("");
    if (newSessionId) {
      patchRecentSessionsAfterSend({
        kind: "new",
        sessionId: newSessionId,
        requestedRepositoryId: repositoryId,
        promptText: text,
        invalidate: false,
      });
      setSelectedSessionId(newSessionId);
      setOpenedSessionId(newSessionId);
      setViewMode("thread");
    }
    sendMutation.mutate({
      optimisticId,
      text,
      sessionId,
      newSessionId,
      repositoryId,
      model: effectiveModel,
      thinkingVariant: effectiveThinkingVariant,
      sendStartedAt: startedAt,
    });
  }, [
    composerStatus.state,
    fallbackRepo,
    openedSessionId,
    patchRecentSessionsAfterSend,
    sendMutation,
    effectiveModel,
    effectiveThinkingVariant,
    viewMode,
  ]);

  const submit = useCallback(() => {
    submitText({ text: draft });
  }, [draft, submitText]);

  // ---------------------------------------------------------------------------
  // Voice dictation — /voice starts recording; Enter stops, transcribes, sends.
  // ---------------------------------------------------------------------------
  const voiceUploadSessionId = viewMode === "thread" && openedSessionId
    ? openedSessionId
    : AGENT_VIEW_DRAFT_VOICE_SESSION_ID;

  const handleVoiceTranscript = useCallback((transcript: string) => {
    submitText({ text: transcript });
    inputRef.current?.focus();
  }, [submitText]);

  const {
    isSupported: voiceSupported,
    isRecording,
    isTranscribing,
    elapsedMs,
    error: voiceError,
    mediaStream,
    startRecording,
    stopRecording,
    cancelVoiceInput,
    retryTranscription,
    clearError: clearVoiceError,
    dismissError: dismissVoiceError,
  } = useVoiceTranscription({
    sessionId: voiceUploadSessionId,
    onTranscript: handleVoiceTranscript,
  });

  const isVoiceInputBlocked = isRecording || isTranscribing;

  const beginVoiceRecording = useCallback(() => {
    if (composerStatus.state === "sending" || isTranscribing) return;
    if (!voiceSupported) {
      clearVoiceError();
      void startRecording();
      return;
    }
    if (viewMode !== "thread" && !fallbackRepo) {
      setComposerStatus({
        state: "failed",
        startedAt: Date.now(),
        errorMessage: "Connect a repository first.",
        sentToSessionId: null,
        sentKind: null,
      });
      return;
    }
    clearVoiceError();
    void startRecording().then(() => {
      inputRef.current?.focus();
    });
  }, [clearVoiceError, composerStatus.state, fallbackRepo, isTranscribing, startRecording, viewMode, voiceSupported]);

  const dispatchVoiceCommand = useCallback(() => {
    if (isRecording) {
      stopRecording();
      inputRef.current?.focus();
      return;
    }
    beginVoiceRecording();
  }, [beginVoiceRecording, isRecording, stopRecording]);

  const cancelVoiceAndRefocus = useCallback(() => {
    cancelVoiceInput();
    inputRef.current?.focus();
  }, [cancelVoiceInput]);

  const selectModelMenuOption = useCallback((index: number) => {
    const option = modelMenuOptions[index];
    if (!option) return;
    if (option.kind === "thinking" && option.disabled) return;

    if (option.kind === "model") {
      const [, providerID, modelID] = option.id.split(":");
      if (!providerID || !modelID) return;
      const nextModel = { providerID, modelID };
      const modelDetail = findModelDetail({
        providers: agentViewProviders,
        model: nextModel,
      });
      const rememberedVariant = normalizeThinkingVariant(getModelVariantPref(nextModel));
      const carriedVariant = normalizeThinkingVariant(selectedThinkingVariant);
      const nextVariant = resolveEffectiveThinkingVariant({
        providerID,
        modelID,
        requestedVariant: rememberedVariant ?? carriedVariant,
        runtimeVariants: modelDetail?.variants,
      });
      setSelectedModel(nextModel);
      setSelectedThinkingVariant(nextVariant);
      setModelVariantPref(nextModel, nextVariant ?? undefined);
      pushRecentModel(nextModel);
      return;
    }

    const [, rawVariant] = option.id.split(":");
    const nextVariant = normalizeThinkingVariant(rawVariant);
    setSelectedThinkingVariant(nextVariant);
    if (effectiveModel) {
      setModelVariantPref(effectiveModel, nextVariant ?? undefined);
    }
    setModelMenuOpen(false);
    inputRef.current?.focus();
  }, [
    agentViewProviders,
    effectiveModel,
    modelMenuOptions,
    getModelVariantPref,
    pushRecentModel,
    selectedThinkingVariant,
    setModelVariantPref,
  ]);

  const handleModelMenuKeyDown = useCallback(
    (event: KeyboardEvent<HTMLInputElement>): boolean => {
      if (!modelMenuOpen) return false;
      switch (event.key) {
        case "ArrowDown":
          event.preventDefault();
          setModelMenuActiveIndex((current) =>
            modelMenuOptions.length === 0
              ? 0
              : (current + MODEL_MENU_NAVIGATION_STEP) % modelMenuOptions.length,
          );
          return true;
        case "ArrowUp":
          event.preventDefault();
          setModelMenuActiveIndex((current) =>
            modelMenuOptions.length === 0
              ? 0
              : (current - MODEL_MENU_NAVIGATION_STEP + modelMenuOptions.length) % modelMenuOptions.length,
          );
          return true;
        case "Enter":
          event.preventDefault();
          selectModelMenuOption(modelMenuActiveIndex);
          return true;
        case "Escape":
          event.preventDefault();
          setModelMenuOpen(false);
          return true;
        case "Tab":
          event.preventDefault();
          return true;
        default:
          return false;
      }
    },
    [modelMenuActiveIndex, modelMenuOpen, modelMenuOptions.length, selectModelMenuOption],
  );

  // ---------------------------------------------------------------------------
  // View transitions
  // ---------------------------------------------------------------------------
  const enterThreadView = useCallback((sessionId: string) => {
    queryClient.invalidateQueries({ queryKey: ["agent-view", "recent-sessions"] });
    queryClient.invalidateQueries({ queryKey: ["agent-view", "transcript", sessionId] });
    queryClient.invalidateQueries({ queryKey: ["agent-view", "status", sessionId] });
    setSelectedSessionId(sessionId);
    setOpenedSessionId(sessionId);
    setViewMode("thread");
    inputRef.current?.focus();
  }, [queryClient]);

  const exitThreadView = useCallback(() => {
    setViewMode("list");
    setOpenedSessionId(null);
    setArchiveConfirmation(null);
    inputRef.current?.focus();
  }, []);

  const enterExpandedView = useCallback(() => {
    setViewMode("expanded");
    setArchiveConfirmation(null);
  }, []);

  const exitFullScreenView = useCallback(() => {
    setViewMode("list");
    inputRef.current?.focus();
  }, []);

  // ---------------------------------------------------------------------------
  // Slash command wiring for the *main* composer
  // ---------------------------------------------------------------------------
  const onCommandDispatch = useCallback(
    (args: { command: SlashCommandDefinition; available: boolean }) => {
      if (!args.available) return;
      switch (args.command.id) {
        case "expand":
          enterExpandedView();
          return;
        case "repository":
          // Picker is opened by the hook itself.
          return;
        case "voice":
          dispatchVoiceCommand();
          return;
        case "model":
          setModelMenuOpen(true);
          setModelMenuActiveIndex(0);
          return;
      }
    },
    [dispatchVoiceCommand, enterExpandedView],
  );

  const composer = useAgentViewComposer({
    draft,
    setDraft,
    viewMode,
    repos,
    reposLoading: reposQuery.isLoading,
    selectedRepoId: fallbackRepo?.id ?? null,
    onPickRepo: (id) => setPendingRepoId(id),
    onCommandDispatch,
  });

  // ---------------------------------------------------------------------------
  // Archive confirmation helpers
  // ---------------------------------------------------------------------------
  const openArchiveConfirmation = useCallback((sessionId: string) => {
    setArchiveConfirmation({ sessionId, choice: "yes" });
  }, []);

  const confirmArchiveSelection = useCallback(() => {
    if (archiveConfirmation === null) return;
    if (archiveConfirmation.choice === "no") {
      setArchiveConfirmation(null);
      return;
    }
    const sessionId = archiveConfirmation.sessionId;
    setArchiveConfirmation(null);
    archiveMutation.mutate({ sessionId });
    if (openedSessionId === sessionId) {
      exitThreadView();
    }
  }, [archiveConfirmation, archiveMutation, exitThreadView, openedSessionId]);

  // ---------------------------------------------------------------------------
  // Keyboard navigation
  // ---------------------------------------------------------------------------
  const moveSelection = useCallback(
    (delta: number) => {
      if (flatList.length === 0) return;
      const currentIndex = flatList.findIndex((row) => row.id === selectedSessionId);
      const nextIndex = currentIndex < 0
        ? (delta > 0 ? 0 : flatList.length - 1)
        : Math.max(0, Math.min(flatList.length - 1, currentIndex + delta));
      setSelectedSessionId(flatList[nextIndex].id);
    },
    [flatList, selectedSessionId],
  );

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLInputElement>) => {
      if (isRecording) {
        event.preventDefault();
        if (event.key === "Enter") {
          stopRecording();
          return;
        }
        if (event.key === "Escape") {
          cancelVoiceAndRefocus();
        }
        return;
      }

      if (isTranscribing) {
        event.preventDefault();
        if (event.key === "Escape") {
          cancelVoiceAndRefocus();
        }
        return;
      }

      if (voiceError && event.key === "Escape") {
        event.preventDefault();
        dismissVoiceError();
        return;
      }

      // 1) Model popup and slash/repo menus — these own arrows/Enter while open.
      const handledModelMenu = handleModelMenuKeyDown(event);
      if (handledModelMenu) return;

      const handled = composer.handleMenuKeyDown(event);
      if (handled) return;

      // 2) Archive confirmation modal owns its own ↑↓/Enter/Esc.
      if (archiveConfirmation !== null) {
        switch (event.key) {
          case "ArrowLeft":
          case "ArrowRight":
          case "ArrowUp":
          case "ArrowDown":
            event.preventDefault();
            setArchiveConfirmation((current) => current === null
              ? current
              : { ...current, choice: current.choice === "yes" ? "no" : "yes" });
            return;
          case "Enter":
            event.preventDefault();
            confirmArchiveSelection();
            return;
          case "Escape":
            event.preventDefault();
            setArchiveConfirmation(null);
            return;
          default:
            event.preventDefault();
            return;
        }
      }

      if (helpOpen && event.key !== "Escape" && event.key !== "?") return;

      // Ctrl/Cmd+X — archive currently selected. Only when composer empty so
      // native Cmd+X cut still works mid-sentence.
      if (
        (event.ctrlKey || event.metaKey) &&
        event.key.toLowerCase() === "x" &&
        draft.length === 0
      ) {
        if (viewMode === "thread" && openedSession) {
          event.preventDefault();
          archiveMutation.mutate({ sessionId: openedSession.id });
          exitThreadView();
        } else if (viewMode === "list" && selectedSession) {
          event.preventDefault();
          archiveMutation.mutate({ sessionId: selectedSession.id });
        }
        return;
      }

      switch (event.key) {
        case "ArrowUp":
          if (viewMode === "list") {
            event.preventDefault();
            moveSelection(-1);
          }
          return;
        case "ArrowDown":
          if (viewMode === "list") {
            event.preventDefault();
            moveSelection(1);
          }
          return;
        case "Enter": {
          event.preventDefault();
          if (draft.trim().length > 0) {
            submit();
            return;
          }
          if (viewMode === "list" && selectedSession) {
            enterThreadView(selectedSession.id);
          }
          return;
        }
        case "Backspace": {
          if (viewMode === "list" && draft.length === 0 && selectedSession) {
            event.preventDefault();
            openArchiveConfirmation(selectedSession.id);
          }
          return;
        }
        case "Escape": {
          if (helpOpen) {
            event.preventDefault();
            setHelpOpen(false);
            return;
          }
          if (composerStatus.state === "failed") {
            event.preventDefault();
            setComposerStatus(buildIdleComposerStatus());
            return;
          }
          if (viewMode === "thread") {
            event.preventDefault();
            exitThreadView();
          }
          return;
        }
        case "?": {
          if (draft.length === 0) {
            event.preventDefault();
            setHelpOpen((open) => !open);
          }
          return;
        }
      }
    },
    [
      archiveConfirmation,
      archiveMutation,
      cancelVoiceAndRefocus,
      composer,
      composerStatus.state,
      confirmArchiveSelection,
      dismissVoiceError,
      draft,
      enterThreadView,
      exitThreadView,
      helpOpen,
      handleModelMenuKeyDown,
      isRecording,
      isTranscribing,
      moveSelection,
      openArchiveConfirmation,
      openedSession,
      selectedSession,
      stopRecording,
      submit,
      voiceError,
      viewMode,
    ],
  );

  // Auto-scroll the highlighted row into view as the user navigates the list.
  useEffect(() => {
    if (viewMode !== "list" || !selectedSessionId) return;
    const el = listRef.current?.querySelector<HTMLElement>(
      `[data-agent-view-row="${selectedSessionId}"]`,
    );
    if (el) el.scrollIntoView({ block: "nearest", behavior: "smooth" });
  }, [selectedSessionId, viewMode]);

  // Mount focus.
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // When transitioning to expanded mode the main composer disappears, so
  // close any open menus that depend on it. When coming back from expanded
  // mode, re-focus the input so keyboard navigation works immediately.
  useEffect(() => {
    if (viewMode === "expanded") {
      composer.closeMenus();
      setModelMenuOpen(false);
      return;
    }
    inputRef.current?.focus();
    // Intentionally exclude `composer` from the dep array — we only want
    // this to run when viewMode flips, not on every menu state change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewMode]);

  const openThreadIsWorking =
    hasPendingOptimisticForOpened || openThreadDoStatus?.activityState === "running";

  // ---------------------------------------------------------------------------
  // Header copy
  // ---------------------------------------------------------------------------
  const headerStats = useMemo(() => {
    if (viewMode === "thread" && openedSession) {
      if (hasPendingOptimisticForOpened) return "agent is working…";
      if (openThreadDoStatus) {
        if (openThreadDoStatus.activityState === "running") return "agent is working…";
        if (openThreadDoStatus.activityState === "error") return "agent errored — needs input";
        if (openThreadDoStatus.lastFinishReason === "waiting_input") return "waiting for your input";
        if (openThreadDoStatus.prState === "merged") return "PR merged";
        if (openThreadDoStatus.prState === "open" || openThreadDoStatus.prState === "draft") return "PR open";
        return "idle";
      }
      const bucket = deriveSessionBucket({
        status: openedSession.status,
        lifecycleStatus: openedSession.lifecycleStatus,
        prState: openedSession.prState,
        doSnapshot: openedSession.doStatusSnapshot ?? null,
      });
      return BUCKET_LABELS[bucket].toLowerCase();
    }
    const parts: string[] = [];
    if (stats.needs_input > 0) parts.push(`${stats.needs_input} awaiting input`);
    if (stats.working > 0) parts.push(`${stats.working} working`);
    if (stats.completed > 0) parts.push(`${stats.completed} completed`);
    if (parts.length === 0) return "no active threads";
    return parts.join(" · ");
  }, [hasPendingOptimisticForOpened, openThreadDoStatus, openedSession, stats, viewMode]);

  const chromeStats = useMemo(() => {
    if (viewMode === "thread") return headerStats;
    return `${formatRepositoryCount({ count: repos.length })} · ${headerStats}`;
  }, [headerStats, repos.length, viewMode]);

  // ---------------------------------------------------------------------------
  // Composer hint copy
  // ---------------------------------------------------------------------------
  const composerHint = (() => {
    if (isRecording) {
      return `recording ${formatAgentViewRecordingDuration({ elapsedMs })} · enter to transcribe + send · esc cancel`;
    }
    if (isTranscribing) return "transcribing audio…";
    if (voiceError) return voiceError.message;
    if (modelMenuOpen) return "↑↓ navigate · ↵ select · esc close";
    if (composer.mode === "slash") return "↑↓ navigate · ↵ run · esc cancel";
    if (composer.mode === "repo-picker") return "type to filter · ↑↓ navigate · ↵ select · esc cancel";
    if (composerStatus.state === "sending") return "sending…";
    if (composerStatus.state === "sent") {
      const verb = composerStatus.sentKind === "new" ? "queued new sandbox" : "queued reply";
      return `${verb} ✓`;
    }
    if (composerStatus.state === "failed") {
      return composerStatus.errorMessage ?? "failed";
    }
    if (viewMode === "thread") {
      return "esc to go back · enter to send · / for commands";
    }
    return "↑↓ navigate · enter open · backspace archive · / commands · ? help";
  })();

  const composerHintTone = (() => {
    if (isRecording) return "text-red-300";
    if (isTranscribing) return "text-amber-300";
    if (voiceError) return "text-rose-400";
    if (modelMenuOpen) return "text-sky-300/80";
    if (composer.mode !== "normal") return "text-sky-300/80";
    if (composerStatus.state === "sending") return "text-amber-300";
    if (composerStatus.state === "sent") return "text-emerald-300";
    if (composerStatus.state === "failed") return "text-rose-400";
    if (viewMode === "thread") return "text-sky-300/80";
    return "text-zinc-600";
  })();

  const composerPrefix = (() => {
    if (composer.mode === "repo-picker") return "?";
    if (viewMode === "thread") return "↳";
    return "›";
  })();

  const composerPlaceholder = (() => {
    if (isRecording) return "recording voice… press Enter to send";
    if (isTranscribing) return "transcribing audio…";
    if (composer.mode === "repo-picker") return "filter repositories…";
    if (viewMode === "thread") return "type a follow-up message… ( / for commands )";
    return "describe a task — or /voice";
  })();

  // The active menu rendered above the input. Computed here so the JSX below
  // stays mostly declarative.
  const composerMenu: ReactNode = (() => {
    if (modelMenuOpen) {
      return (
        <AgentViewModelMenu
          options={modelMenuOptions}
          activeIndex={modelMenuActiveIndex}
          loading={providersQuery.isLoading || Boolean(providersQuery.data?.loadingModels)}
          onHover={setModelMenuActiveIndex}
          onSelect={selectModelMenuOption}
        />
      );
    }
    if (composer.mode === "slash") {
      return (
        <SlashCommandMenu
          matches={composer.slashMatches}
          activeIndex={composer.slashActiveIndex}
          onHover={(index) => composer.setSlashActiveIndex(index)}
          onSelect={(index) => composer.selectSlashAtIndex(index)}
          noMatchHint="no matching command"
        />
      );
    }
    if (composer.mode === "repo-picker") {
      return (
        <RepoPickerMenu
          matches={composer.repoMatches}
          activeIndex={composer.repoActiveIndex}
          selectedRepoId={fallbackRepo?.id ?? null}
          query={composer.repoQuery}
          onHover={(index) => composer.setRepoActiveIndex(index)}
          onSelect={(index) => composer.selectRepoAtIndex(index)}
          loading={reposQuery.isLoading}
        />
      );
    }
    return null;
  })();

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  const isFullScreenMode = viewMode === "expanded";

  return (
    <div className="agent-view-mono flex h-full items-center justify-center bg-[#0b0b0d] px-4 py-6 text-zinc-200 selection:bg-amber-300/30">
      {viewMode === "expanded" ? (
        <OpenCodeTerminalsView
          sessions={displaySessions}
          onCompact={exitFullScreenView}
        />
      ) : null}

      <div
        className={cn(
          "relative flex max-h-[min(80vh,640px)] w-full max-w-[760px] flex-col overflow-hidden rounded-xl border border-zinc-800/80 bg-[#0d0d10] shadow-[0_30px_80px_-30px_rgba(0,0,0,0.65)]",
          // Re-trigger a soft scale entrance whenever we re-enter list/thread
          // mode after expanding then returning. The animation key is keyed on
          // viewMode so React re-mounts the wrapper.
          !isFullScreenMode &&
            "animate-[agent-view-card-return_180ms_ease-out]",
        )}
        // Hide the centered card from a11y tree while a full-screen overlay
        // owns the screen, but keep it mounted so its state survives the
        // round trip.
        aria-hidden={isFullScreenMode}
        style={isFullScreenMode ? { visibility: "hidden" } : undefined}
      >
        <div className="flex items-center gap-3 border-b border-zinc-800/60 px-4 py-2.5">
          <span className="text-xs uppercase tracking-[0.18em] text-zinc-500">
            cord agent view
          </span>
          <span aria-hidden className="h-3 w-px bg-zinc-800" />
          <span className="min-w-0 truncate text-xs text-zinc-600">
            {recentSessionsQuery.isLoading ? "loading…" : chromeStats}
          </span>
          <div className="ml-auto flex items-center gap-2 text-xs text-zinc-600">
            {recentSessionsQuery.isFetching ? (
              <span className="inline-flex items-center gap-1">
                <span className="size-1 animate-pulse rounded-full bg-amber-400" />
                refreshing
              </span>
            ) : null}
          </div>
        </div>

        {/* Content area — switches between list and thread. */}
        <div className="flex min-h-[420px] flex-1 flex-col overflow-hidden px-5 pt-4">
          {viewMode === "thread" ? (
            <ThreadView
              ref={threadScrollerRef}
              loading={transcriptQuery.isLoading}
              messages={threadDisplay}
              session={openedSession}
              userFirstName={userFirstName}
              working={openThreadIsWorking}
            />
          ) : (
            // We render the list view in full-screen mode too. The centered
            // card is `visibility: hidden` anyway, so this just keeps state
            // warm for the round trip.
            <ListView
              ref={listRef}
              loading={recentSessionsQuery.isLoading}
              flatList={flatList}
              grouped={grouped}
              selectedSessionId={selectedSessionId}
              now={now}
              rowSummaryBySessionId={rowSummaryBySessionId}
              userFirstName={userFirstName}
              onSelect={(id) => setSelectedSessionId(id)}
              onActivate={(row) => enterThreadView(row.id)}
            />
          )}
        </div>

        <Composer
          inputRef={inputRef}
          draft={draft}
          onChangeDraft={(value) => {
            if (isVoiceInputBlocked) return;
            if (composerStatus.state === "failed") {
              setComposerStatus(buildIdleComposerStatus());
            }
            setModelMenuOpen(false);
            setDraft(value);
          }}
          onKeyDown={handleKeyDown}
          composerStatus={composerStatus}
          prefix={composerPrefix}
          hint={composerHint}
          hintTone={composerHintTone}
          modelStatusLabel={modelStatusLabel}
          placeholder={composerPlaceholder}
          readOnly={isVoiceInputBlocked}
          repos={repos}
          selectedRepoId={fallbackRepo?.id ?? null}
          onPickRepo={(id) => setPendingRepoId(id)}
          // Hide the legacy <select> repo picker once the user opens the new
          // slash-command-driven one — keeps the bottom row uncluttered.
          showRepoPicker={viewMode === "list" && composer.mode === "normal"}
          repoPickerLoading={reposQuery.isLoading}
          menu={composerMenu}
          voiceStatus={(
            <AgentViewVoiceStatus
              isRecording={isRecording}
              isTranscribing={isTranscribing}
              elapsedMs={elapsedMs}
              error={voiceError}
              mediaStream={mediaStream}
              onCancel={cancelVoiceAndRefocus}
              onRetry={retryTranscription}
              onDismiss={dismissVoiceError}
            />
          )}
        />

        {archiveConfirmation ? (
          <ArchiveConfirmationOverlay
            choice={archiveConfirmation.choice}
            onChoose={(choice) => setArchiveConfirmation((current) => current === null ? current : { ...current, choice })}
            onConfirm={confirmArchiveSelection}
            onCancel={() => setArchiveConfirmation(null)}
          />
        ) : null}
        {helpOpen ? <HelpOverlay onClose={() => setHelpOpen(false)} /> : null}
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Subcomponents
// -----------------------------------------------------------------------------

function ListView({
  ref: scrollRef,
  loading,
  flatList,
  grouped,
  selectedSessionId,
  now,
  rowSummaryBySessionId,
  userFirstName,
  onSelect,
  onActivate,
}: {
  ref: React.RefObject<HTMLDivElement | null>;
  loading: boolean;
  flatList: AgentViewRecentSession[];
  grouped: Record<SessionBucket, AgentViewRecentSession[]>;
  selectedSessionId: string | null;
  now: number;
  rowSummaryBySessionId: Map<string, string>;
  userFirstName: string;
  onSelect: (id: string) => void;
  onActivate: (row: AgentViewRecentSession) => void;
}) {
  return (
    <div
      ref={scrollRef}
      className="mt-4 min-h-0 flex-1 overflow-y-auto pr-1 [scrollbar-width:thin]"
    >
      {loading ? (
        <LoadingRows />
      ) : flatList.length === 0 ? (
        <ListEmptyState userFirstName={userFirstName} />
      ) : (
        BUCKET_ORDER.map((bucket) => {
          const rows = grouped[bucket];
          if (rows.length === 0) return null;
          return (
            <SessionGroup
              key={bucket}
              bucket={bucket}
              rows={rows}
              selectedSessionId={selectedSessionId}
              onSelect={onSelect}
              onActivate={onActivate}
              now={now}
              rowSummaryBySessionId={rowSummaryBySessionId}
            />
          );
        })
      )}
    </div>
  );
}

type ThreadDisplayItem =
  | { kind: "transcript"; message: TranscriptMessage; key: string }
  | { kind: "optimistic"; entry: OptimisticEntry; key: string };

function ThreadView({
  ref: scrollRef,
  loading,
  messages,
  session,
  userFirstName,
  working,
}: {
  ref: React.RefObject<HTMLDivElement | null>;
  loading: boolean;
  messages: ThreadDisplayItem[];
  session: AgentViewRecentSession | null;
  userFirstName: string;
  working: boolean;
}) {
  return (
    <div
      ref={scrollRef}
      className="mt-4 min-h-0 flex-1 overflow-y-auto pr-1 [scrollbar-width:thin]"
    >
      {loading && messages.length === 0 ? (
        <LoadingRows />
      ) : messages.length === 0 && !working ? (
        <ThreadEmptyState session={session} userFirstName={userFirstName} />
      ) : (
        <ul className="flex flex-col gap-3 pb-2">
          {messages.map((item) =>
            item.kind === "transcript" ? (
              <TranscriptBubble key={item.key} message={item.message} />
            ) : (
              <OptimisticBubble key={item.key} entry={item.entry} />
            ),
          )}
          {working ? <ThreadWorkingIndicator /> : null}
        </ul>
      )}
    </div>
  );
}

function ThreadWorkingIndicator() {
  return (
    <li className="flex flex-col gap-0.5">
      <div className="text-xs uppercase tracking-[0.16em] text-zinc-600">
        agent
      </div>
      <div className="inline-flex items-center gap-2 text-[12.5px] text-amber-300">
        <span className="inline-flex items-center gap-0.5" aria-hidden>
          <span className="size-1 animate-[agent-view-dot-pulse_1s_ease-in-out_infinite] rounded-full bg-amber-300" />
          <span className="size-1 animate-[agent-view-dot-pulse_1s_ease-in-out_infinite_0.15s] rounded-full bg-amber-300" />
          <span className="size-1 animate-[agent-view-dot-pulse_1s_ease-in-out_infinite_0.3s] rounded-full bg-amber-300" />
        </span>
        thinking / working…
      </div>
    </li>
  );
}

function TranscriptBubble({ message }: { message: TranscriptMessage }) {
  const isUser = message.role === "user";
  const prefix = isUser ? "you" : "agent";
  const tone = isUser ? "text-zinc-100" : "text-zinc-300";
  return (
    <li className="flex flex-col gap-0.5">
      <div className="text-xs uppercase tracking-[0.16em] text-zinc-600">
        {prefix}
      </div>
      <pre
        className={cn(
          "whitespace-pre-wrap break-words text-[12.5px] leading-relaxed",
          tone,
        )}
      >
        {message.content}
      </pre>
    </li>
  );
}

function OptimisticBubble({ entry }: { entry: OptimisticEntry }) {
  const indicator = (() => {
    if (entry.status === "sending") {
      return (
        <span className="inline-flex items-center gap-1 text-amber-300">
          <span className="size-1 animate-[agent-view-dot-pulse_1s_ease-in-out_infinite] rounded-full bg-amber-300" />
          sending
        </span>
      );
    }
    if (entry.status === "sent") {
      return <span className="text-emerald-300">sent ✓</span>;
    }
    return <span className="text-rose-400">{entry.errorMessage ?? "failed"}</span>;
  })();
  return (
    <li className="flex flex-col gap-0.5">
      <div className="flex items-center gap-2 text-xs uppercase tracking-[0.16em]">
        <span className="text-zinc-600">you</span>
        <span aria-hidden className="text-zinc-700">·</span>
        {indicator}
      </div>
      <pre
        className={cn(
          "whitespace-pre-wrap break-words text-[12.5px] leading-relaxed",
          entry.status === "failed" ? "text-rose-200/80" : "text-zinc-100",
        )}
      >
        {entry.text}
      </pre>
    </li>
  );
}

function SessionGroup({
  bucket,
  rows,
  selectedSessionId,
  onSelect,
  onActivate,
  now,
  rowSummaryBySessionId,
}: {
  bucket: SessionBucket;
  rows: AgentViewRecentSession[];
  selectedSessionId: string | null;
  onSelect: (id: string) => void;
  onActivate: (row: AgentViewRecentSession) => void;
  now: number;
  rowSummaryBySessionId: Map<string, string>;
}) {
  return (
    <section className="mb-4 last:mb-0">
      <div className="mb-1 text-xs font-medium text-zinc-400">
        {BUCKET_LABELS[bucket]}
      </div>
      <div className="flex flex-col">
        {rows.map((row) => (
          <SessionRow
            key={row.id}
            row={row}
            bucket={bucket}
            selected={row.id === selectedSessionId}
            onSelect={() => onSelect(row.id)}
            onActivate={() => onActivate(row)}
            now={now}
            rowSummary={rowSummaryBySessionId.get(row.id) ?? null}
          />
        ))}
      </div>
    </section>
  );
}

function SessionRow({
  row,
  bucket,
  selected,
  onSelect,
  onActivate,
  now,
  rowSummary,
}: {
  row: AgentViewRecentSession;
  bucket: SessionBucket;
  selected: boolean;
  onSelect: () => void;
  onActivate: () => void;
  now: number;
  rowSummary: string | null;
}) {
  const description = useMemo(
    () => buildSessionDescription({ session: row, bucket, rowSummary }),
    [row, bucket, rowSummary],
  );
  const time = useMemo(
    () => formatRelativeTime({ now, thenMs: new Date(row.updatedAt).getTime() }),
    [now, row.updatedAt],
  );
  const title = (row.title?.trim() || "(untitled)").toLowerCase();

  const renderState = useMemo(
    () =>
      deriveAgentViewRenderState({
        status: row.status,
        lifecycleStatus: row.lifecycleStatus,
        prState: row.prState,
        doSnapshot: row.doStatusSnapshot ?? null,
      }),
    [row.doStatusSnapshot, row.lifecycleStatus, row.prState, row.status],
  );

  const indicator = <AgentViewStatusIndicator renderState={renderState} />;

  return (
    <button
      type="button"
      data-agent-view-row={row.id}
      onMouseEnter={onSelect}
      onClick={onActivate}
      className={cn(
        "group flex items-center gap-3 px-2 py-[3px] text-left text-sm transition-colors",
        selected
          ? "bg-zinc-100/[0.04] text-zinc-100"
          : "text-zinc-300 hover:text-zinc-100",
      )}
    >
      <span aria-hidden className="shrink-0">{indicator}</span>
      <span className="w-[180px] shrink-0 truncate">
        <span
          className={cn(
            "px-1",
            selected ? "bg-zinc-100 text-zinc-900" : "text-zinc-200",
          )}
        >
          {title}
        </span>
      </span>
      <span className="min-w-0 flex-1 truncate text-zinc-500">{description}</span>
      <span className="shrink-0 tabular-nums text-zinc-500">{time}</span>
    </button>
  );
}

function Composer({
  inputRef,
  draft,
  onChangeDraft,
  onKeyDown,
  composerStatus,
  prefix,
  hint,
  hintTone,
  modelStatusLabel,
  placeholder,
  readOnly,
  repos,
  selectedRepoId,
  onPickRepo,
  showRepoPicker,
  repoPickerLoading,
  menu,
  voiceStatus,
}: {
  inputRef: React.RefObject<HTMLInputElement | null>;
  draft: string;
  onChangeDraft: (next: string) => void;
  onKeyDown: (event: KeyboardEvent<HTMLInputElement>) => void;
  composerStatus: ComposerStatus;
  prefix: string;
  hint: string;
  hintTone: string;
  modelStatusLabel: string;
  placeholder: string;
  readOnly?: boolean;
  repos: AgentViewRepo[];
  selectedRepoId: string | null;
  onPickRepo: (id: string) => void;
  showRepoPicker: boolean;
  repoPickerLoading: boolean;
  // Optional floating menu rendered immediately above the input — used for
  // the slash-command autocomplete and the inline `/repository` picker.
  menu?: ReactNode;
  voiceStatus?: ReactNode;
}) {
  const sending = composerStatus.state === "sending";
  return (
    <div className="relative border-t border-zinc-800/80 bg-[#0a0a0c] px-5 pb-3 pt-3">
      {menu}
      {voiceStatus}
      <div className="flex items-center gap-2 text-sm">
        <span
          className={cn(
            "shrink-0 select-none text-zinc-500",
            sending && "text-amber-300",
          )}
        >
          {prefix}
        </span>
        <div className="relative flex flex-1 items-center">
          <input
            ref={inputRef}
            type="text"
            spellCheck={false}
            autoComplete="off"
            value={draft}
            readOnly={readOnly}
            onChange={(event) => onChangeDraft(event.target.value)}
            onKeyDown={onKeyDown}
            placeholder={placeholder}
            className={cn(
              "min-w-0 flex-1 bg-transparent text-zinc-100 placeholder:text-zinc-700 focus:outline-none",
              sending && "opacity-60",
              readOnly && "cursor-default opacity-80",
            )}
          />
          {sending ? <SendingPulse /> : null}
        </div>
      </div>
      <div className="mt-1 flex items-center justify-between gap-2 text-[10.5px]">
        <span className={cn("truncate", hintTone)}>{hint}</span>
        <div className="flex min-w-0 items-center gap-2">
          <span className="truncate text-zinc-600">{modelStatusLabel}</span>
          {showRepoPicker && repos.length > 1 ? (
            <select
              disabled={repoPickerLoading}
              value={selectedRepoId ?? ""}
              onChange={(event) => onPickRepo(event.target.value)}
              className="bg-transparent text-xs text-zinc-500 focus:outline-none"
              aria-label="Repository for new sessions"
            >
              {repos.map((repo) => (
                <option key={repo.id} value={repo.id} className="bg-zinc-950">
                  {repo.githubFullName}
                </option>
              ))}
            </select>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function SendingPulse() {
  return (
    <span className="pointer-events-none absolute inset-y-0 right-1 flex items-center gap-0.5 text-amber-300">
      <span className="size-1 animate-[agent-view-dot-pulse_1s_ease-in-out_infinite] rounded-full bg-amber-300" />
      <span className="size-1 animate-[agent-view-dot-pulse_1s_ease-in-out_infinite_0.15s] rounded-full bg-amber-300" />
      <span className="size-1 animate-[agent-view-dot-pulse_1s_ease-in-out_infinite_0.3s] rounded-full bg-amber-300" />
    </span>
  );
}

function ArchiveConfirmationOverlay({
  choice,
  onChoose,
  onConfirm,
  onCancel,
}: {
  choice: "yes" | "no";
  onChoose: (choice: "yes" | "no") => void;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  return (
    <div className="absolute inset-0 z-30 flex items-center justify-center bg-black/65 backdrop-blur-sm">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="archive-thread-title"
        className="w-[min(340px,calc(100%-2rem))] rounded-lg border border-zinc-800 bg-zinc-950 p-4 shadow-xl"
      >
        <div id="archive-thread-title" className="text-[12.5px] font-semibold text-zinc-100">
          Archive this thread?
        </div>
        <div className="mt-1 text-[11.5px] leading-relaxed text-zinc-500">
          It will disappear from Agent View. Use arrows to choose, Enter to confirm.
        </div>
        <div className="mt-4 grid grid-cols-2 gap-2">
          <button
            type="button"
            onMouseEnter={() => onChoose("yes")}
            onClick={onConfirm}
            className={cn(
              "rounded-md px-3 py-1.5 text-[12px] font-medium transition-colors",
              choice === "yes"
                ? "bg-zinc-100 text-zinc-950"
                : "bg-zinc-900 text-zinc-400 hover:bg-zinc-800 hover:text-zinc-100",
            )}
          >
            yes
          </button>
          <button
            type="button"
            onMouseEnter={() => onChoose("no")}
            onClick={onCancel}
            className={cn(
              "rounded-md px-3 py-1.5 text-[12px] font-medium transition-colors",
              choice === "no"
                ? "bg-zinc-100 text-zinc-950"
                : "bg-zinc-900 text-zinc-400 hover:bg-zinc-800 hover:text-zinc-100",
            )}
          >
            no
          </button>
        </div>
      </div>
    </div>
  );
}

function HelpOverlay({ onClose }: { onClose: () => void }) {
  return (
    <div className="absolute inset-0 z-30 flex items-center justify-center bg-black/70 backdrop-blur-sm">
      <div className="w-[min(380px,calc(100%-2rem))] rounded-lg border border-zinc-800 bg-zinc-950 p-5 shadow-xl">
        <div className="mb-3 text-[12px] font-semibold text-zinc-100">Shortcuts</div>
        <ul className="space-y-2 text-[11.5px] text-zinc-300">
          <HelpItem keys="↑ / ↓" label="navigate sessions" />
          <HelpItem keys="enter" label="open the highlighted session" />
          <HelpItem keys="enter" label="send the typed prompt" />
          <HelpItem keys="esc" label="back to list / clear errors" />
          <HelpItem keys="backspace" label="confirm archive for highlighted row" />
          <HelpItem keys="ctrl/⌘ + x" label="archive (composer must be empty)" />
          <HelpItem keys="/" label="slash commands (autocomplete)" />
          <HelpItem keys="?" label="toggle this help" />
        </ul>
        <div className="mt-3 border-t border-zinc-800 pt-3 text-[11px] text-zinc-400">
          <div className="mb-1 font-semibold text-zinc-300">Slash commands</div>
          <ul className="space-y-1">
            <li>
              <span className="text-amber-300">/model</span>
              <span className="text-zinc-500"> — switch model or thinking</span>
            </li>
            <li>
              <span className="text-amber-300">/repository</span>
              <span className="text-zinc-500"> — switch active repo</span>
            </li>
            <li>
              <span className="text-amber-300">/expand</span>
              <span className="text-zinc-500"> — native OpenCode terminal grid</span>
            </li>
            <li>
              <span className="text-amber-300">/voice</span>
              <span className="text-zinc-500"> — record, transcribe, and send</span>
            </li>
          </ul>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="mt-4 w-full rounded-md bg-zinc-100 px-3 py-1.5 text-[12px] font-medium text-zinc-900 hover:bg-white"
        >
          got it
        </button>
      </div>
    </div>
  );
}

function HelpItem({ keys, label }: { keys: string; label: string }) {
  return (
    <li className="flex items-center justify-between gap-3">
      <kbd className="rounded-sm bg-zinc-800/80 px-1.5 py-0.5 text-[10.5px] text-zinc-200 ring-1 ring-zinc-700/60">
        {keys}
      </kbd>
      <span className="flex-1 text-right text-zinc-400">{label}</span>
    </li>
  );
}

function LoadingRows() {
  return (
    <div className="mt-2 space-y-1.5">
      {Array.from({ length: 4 }).map((_, i) => (
        <div key={i} className="flex items-center gap-3 px-2 py-1">
          <span className="size-1 rounded-full bg-zinc-700" />
          <span className="h-3 w-[160px] animate-pulse rounded-sm bg-zinc-800/70" />
          <span className="h-3 flex-1 animate-pulse rounded-sm bg-zinc-800/40" />
          <span className="h-3 w-8 animate-pulse rounded-sm bg-zinc-800/50" />
        </div>
      ))}
    </div>
  );
}

function ListEmptyState({ userFirstName }: { userFirstName: string }) {
  return (
    <div className="mt-6 text-sm text-zinc-500">
      <div className="mb-2 text-zinc-300">hi {userFirstName.toLowerCase()} —</div>
      <div className="leading-relaxed">
        no active threads. type a prompt below and hit{" "}
        <kbd className="rounded-sm bg-zinc-800/80 px-1 py-[1px] text-xs text-zinc-300 ring-1 ring-zinc-700/60">
          enter
        </kbd>{" "}
        to start a new sandbox, or{" "}
        <kbd className="rounded-sm bg-zinc-800/80 px-1 py-[1px] text-xs text-zinc-300 ring-1 ring-zinc-700/60">
          /
        </kbd>{" "}
        to see commands.
      </div>
    </div>
  );
}

function ThreadEmptyState({
  session,
  userFirstName,
}: {
  session: AgentViewRecentSession | null;
  userFirstName: string;
}) {
  return (
    <div className="mt-4 text-[12.5px] leading-relaxed text-zinc-500">
      <div>no messages yet for this thread.</div>
      <div className="mt-1 text-zinc-600">
        hi {userFirstName.toLowerCase()}, type below to send a follow-up to{" "}
        <span className="text-zinc-300">
          {(session?.title?.trim() || "this sandbox").toLowerCase()}
        </span>
        .
      </div>
    </div>
  );
}
